import type { PairManifest } from "./historyReader.js";
import { renderTable, type SortColumn, type SortState } from "./render.js";
import { computeDefaultTargetDate, pickDefaultIndex } from "./targetDate.js";

function parseSort(params: URLSearchParams): SortState | null {
  const column = params.get("sort");
  const order = params.get("order");
  if (
    (column === "buy" || column === "sell") &&
    (order === "asc" || order === "desc")
  ) {
    return { column, order };
  }
  return null;
}

function getPairPickerElements() {
  const picker = document.querySelector<HTMLElement>("#pair-picker");
  const toggle = document.querySelector<HTMLButtonElement>(
    "#pair-picker-toggle",
  );
  const panel = document.querySelector<HTMLElement>("#pair-panel");
  const searchInput = document.querySelector<HTMLInputElement>("#pair-search");
  const optionsList = document.querySelector<HTMLUListElement>("#pair-options");
  if (!picker || !toggle || !panel || !searchInput || !optionsList) return null;
  return { picker, toggle, panel, searchInput, optionsList };
}

function setupPairPicker() {
  const elements = getPairPickerElements();
  if (!elements) return;
  const { picker, toggle, panel, searchInput, optionsList } = elements;

  const items = [...optionsList.querySelectorAll("li")];

  function applyFilter() {
    const query = searchInput.value.trim().toLowerCase();
    for (const li of items) {
      const text = li.textContent?.toLowerCase() ?? "";
      li.hidden = query.length > 0 && !text.includes(query);
    }
  }

  function openPanel() {
    panel.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    searchInput.value = "";
    applyFilter();
    searchInput.focus();
  }

  function closePanel() {
    panel.hidden = true;
    toggle.setAttribute("aria-expanded", "false");
  }

  closePanel();

  toggle.addEventListener("click", () => {
    if (panel.hidden) {
      openPanel();
    } else {
      closePanel();
    }
  });
  searchInput.addEventListener("input", applyFilter);
  searchInput.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closePanel();
      toggle.focus();
    } else if (event.key === "Enter") {
      const firstVisible = items.find((li) => !li.hidden);
      const href = firstVisible?.querySelector("a")?.getAttribute("href");
      if (href) {
        event.preventDefault();
        window.location.href = href;
      }
    }
  });
  document.addEventListener("click", (event) => {
    if (!picker.contains(event.target as Node)) closePanel();
  });
}

setupPairPicker();

function getPagerElements() {
  const prevButton = document.querySelector<HTMLButtonElement>("#prev-date");
  const nextButton = document.querySelector<HTMLButtonElement>("#next-date");
  const dateLabel = document.querySelector<HTMLElement>("#date-label");
  const tableContainer =
    document.querySelector<HTMLElement>("#table-container");
  if (!prevButton || !nextButton || !dateLabel || !tableContainer) return null;
  return { prevButton, nextButton, dateLabel, tableContainer };
}

const pager = document.querySelector<HTMLElement>(".date-pager");
const pairId = pager?.dataset.pairId;
const elements = getPagerElements();

if (pairId && elements) {
  const { prevButton, nextButton, dateLabel, tableContainer } = elements;

  void (async () => {
    const res = await fetch(`/data/${pairId}.json`);
    if (!res.ok) return;
    const manifest = (await res.json()) as PairManifest;
    if (manifest.dates.length === 0) return;

    const defaultIndex = pickDefaultIndex(
      manifest.dates,
      computeDefaultTargetDate(new Date()),
    );

    const initialParams = new URLSearchParams(window.location.search);
    const requestedDate = initialParams.get("date");
    const initialIndex = requestedDate
      ? manifest.dates.indexOf(requestedDate)
      : -1;
    let currentIndex = initialIndex >= 0 ? initialIndex : defaultIndex;
    let sortState: SortState | null = parseSort(initialParams);

    function render(pushState: boolean) {
      const date = manifest.dates[currentIndex] as string;
      dateLabel.textContent = date;
      tableContainer.innerHTML = renderTable(
        manifest.byDate[date] ?? [],
        sortState,
      );

      const prevDate = manifest.dates[currentIndex - 1];
      const nextDate = manifest.dates[currentIndex + 1];
      prevButton.disabled = !prevDate;
      prevButton.textContent = `← ${prevDate ?? "前の日付"}`;
      nextButton.disabled = !nextDate;
      nextButton.textContent = `${nextDate ?? "次の日付"} →`;

      const url = new URL(window.location.href);
      if (currentIndex === defaultIndex) {
        url.searchParams.delete("date");
      } else {
        url.searchParams.set("date", date);
      }
      if (sortState) {
        url.searchParams.set("sort", sortState.column);
        url.searchParams.set("order", sortState.order);
      } else {
        url.searchParams.delete("sort");
        url.searchParams.delete("order");
      }
      const state = { index: currentIndex, sort: sortState };
      if (pushState) {
        window.history.pushState(state, "", url);
      } else {
        window.history.replaceState(state, "", url);
      }
    }

    prevButton.addEventListener("click", () => {
      if (currentIndex > 0) {
        currentIndex -= 1;
        render(true);
      }
    });
    nextButton.addEventListener("click", () => {
      if (currentIndex < manifest.dates.length - 1) {
        currentIndex += 1;
        render(true);
      }
    });
    tableContainer.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        ".sort-button",
      );
      const column = button?.dataset.sortColumn as SortColumn | undefined;
      if (!column) return;
      sortState =
        sortState?.column === column
          ? { column, order: sortState.order === "desc" ? "asc" : "desc" }
          : { column, order: "desc" };
      render(true);
    });
    window.addEventListener("popstate", () => {
      const params = new URLSearchParams(window.location.search);
      const date = params.get("date");
      currentIndex =
        date && manifest.dates.includes(date)
          ? manifest.dates.indexOf(date)
          : defaultIndex;
      sortState = parseSort(params);
      render(false);
    });

    render(false);
  })();
}
